import React from 'react';
import { NavLink, Navigate, useLocation } from 'react-router-dom';
import {
  Loader2, LayoutDashboard, Package, ShoppingBag, Truck, Image as ImageIcon,
  LogOut, Shield, CreditCard, Ticket, Star, ShoppingCart
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/layout/Logo';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true, testid: 'admin-nav-dashboard' },
  { to: '/admin/pedidos', label: 'Pedidos', icon: ShoppingBag, testid: 'admin-nav-orders' },
  { to: '/admin/pagos', label: 'Conciliación Bold', icon: CreditCard, testid: 'admin-nav-payments' },
  { to: '/admin/cupones', label: 'Cupones', icon: Ticket, testid: 'admin-nav-coupons' },
  { to: '/admin/resenas', label: 'Reseñas', icon: Star, testid: 'admin-nav-reviews' },
  { to: '/admin/carritos-abandonados', label: 'Carritos abandonados', icon: ShoppingCart, testid: 'admin-nav-carts' },
  { to: '/admin/productos', label: 'Inventario', icon: Package, testid: 'admin-nav-products' },
  { to: '/admin/envios', label: 'Envíos', icon: Truck, testid: 'admin-nav-shipping' },
  { to: '/admin/imagenes', label: 'Imágenes', icon: ImageIcon, testid: 'admin-nav-images' },
];

const AdminLayout = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FAF9F5] flex flex-col lg:flex-row">
      {/* Mobile top nav */}
      <div className="lg:hidden sticky top-16 z-30 bg-[#1C1917] text-white" data-testid="admin-mobile-nav">
        <div className="flex items-center gap-2 overflow-x-auto px-3 py-2 scrollbar-none">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`${item.testid}-mobile`}
                className={({ isActive }) =>
                  `shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive ? 'bg-[#2A5C43] text-white' : 'text-white/70 bg-white/5'
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {item.label}
              </NavLink>
            );
          })}
          <button onClick={logout} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white/70 bg-white/5" data-testid="admin-logout-mobile">
            <LogOut className="w-3.5 h-3.5" /> Salir
          </button>
        </div>
      </div>

      {/* Sidebar (desktop) */}
      <aside
        className="hidden lg:block sticky top-16 h-[calc(100vh-4rem)] w-72 bg-[#1C1917] text-white"
        data-testid="admin-sidebar"
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#D47A4A]" />
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold">Admin</p>
          </div>
          <div className="bg-white/95 rounded-xl px-3 py-2 inline-flex">
            <Logo size="md" variant="lockup" />
          </div>
          <p className="text-xs text-white/60 mt-3">{user.email}</p>
        </div>

        <nav className="p-4 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#2A5C43] text-white'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 p-4 lg:p-8" data-testid="admin-content">
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
